import React, { ComponentProps, ReactElement, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatDistanceToNow } from '../../utils/format/formatDistanceToNow';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconKebab } from '../icon/IconKebab';
import { Checkbox } from '../input/Checkbox';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { MediaCover } from '../media/MediaCover';
import { Typography } from '../typography/Typography';
import { MediaPreview } from './MediaPreview';
import { PostStatus } from './constants';
import { getContentIcon } from './getContentIcon';
import { getSourceDisplayText } from './getSourceDisplayText';
import type { PostUI } from './types';

const Row = styled(Flex).attrs((props) => ({
    py: 1,
    ...props,
    spacing: 1,
    width: '100%',
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
}))`
    -webkit-user-drag: none;
    user-drag: none;
    user-select: none;
    overflow: hidden;

    &:not(:last-child) {
        border-bottom: 1px solid var(--divider);
    }
`;

const IconWrapper = styled(Box).attrs({
    position: 'absolute',
    p: 0.25,
    borderRadius: 1,
    background: 'var(--control-secondary-background)',
    backdropFilter: 'blur(20px)',
})``;

const ControlColumn = styled(Flex).attrs({
    minWidth: 48,
    flexBasis: 0,
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
})``;

const ContentColumn = styled(Flex).attrs((props) => ({
    px: 1,
    flexGrow: 1,
    py: 0.5,
    alignSelf: 'stretch',
    ...props,
    direction: 'column',
    justifyContent: 'space-between',
}))``;

const DateColumn = styled(Flex).attrs({
    display: { xs: 'none', sm: 'flex' },
    minWidth: 120,
    px: 1,
    flexBasis: 0,
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'flex-end',
})``;

const Indicator = styled(Box).attrs((props) => ({
    ...props,
    minWidth: 4,
    height: 56,
    borderTopRightRadius: '999px',
    borderBottomRightRadius: '999px',
}))``;

const COLOR_MAPPING: Record<PostStatus, string> = {
    [PostStatus.PUBLISHED]: 'var(--color-success)',
    [PostStatus.LIMITED]: 'var(--color-warning)',
    [PostStatus.MODERATION]: 'var(--color-info)',
    [PostStatus.DECLINED]: 'var(--color-danger)',
    [PostStatus.DELETED]: 'var(--color-danger)',
    [PostStatus.DRAFT]: 'var(--placeholder)',
};

interface FeedManagementHeaderProps extends Omit<ComponentProps<typeof Flex>, 'onSelect'> {
    selected: boolean;
    onSelect: (value: boolean) => void;
}

export const FeedManagementHeader = (props: FeedManagementHeaderProps) => {
    const { selected, onSelect, ...other } = props;
    const onToggleSelected = useCallback(() => onSelect(!selected), [selected, onSelect]);
    const t = useTranslation();

    return (
        <Row {...other} py={0}>
            <Indicator />
            <ControlColumn>
                <Checkbox checked={selected} onChange={onToggleSelected} />
            </ControlColumn>
            <Flex width={64} flexShrink={0}>
                <Typography variant="caption" color="secondary">
                    {t('Контент')}
                </Typography>
            </Flex>
            <ContentColumn alignSelf="unset">
                <Typography variant="caption" color="secondary">
                    {t('Описание')}
                </Typography>
            </ContentColumn>
            <DateColumn>
                <Typography variant="caption" color="secondary">
                    {t('Опубликован')}
                </Typography>
            </DateColumn>
            <ControlColumn />
        </Row>
    );
};

interface FeedManagementRowProps extends Omit<ComponentProps<typeof Flex>, 'onSelect'> {
    post: PostUI;
    selected: boolean;
    renderMenu: (trigger: ReactElement) => ReactElement | null;
    onSelect: (value: boolean) => void;
}

export const FeedManagementRow = (props: FeedManagementRowProps) => {
    const { selected, post, renderMenu, onSelect, ...other } = props;
    const onToggleSelected = useCallback(() => onSelect(!selected), [selected, onSelect]);
    const ContentIcon = getContentIcon(post.content);

    return (
        <Row {...other}>
            <Indicator backgroundColor={COLOR_MAPPING[post.status ?? PostStatus.PUBLISHED]} />
            <ControlColumn>
                <Checkbox checked={selected} onChange={onToggleSelected} />
            </ControlColumn>
            <Flex as={Link} to={post.url} position="relative" flexShrink={0} textDecoration="none">
                <MediaCover width={64} ratio={1} borderRadius={1} overflow="hidden">
                    <MediaPreview media={post.content[0]} namespace="grid" />
                </MediaCover>
                {ContentIcon && (
                    <IconWrapper right={0.5} bottom={0.5}>
                        <ContentIcon color="var(--control-secondary-color)" size={20} />
                    </IconWrapper>
                )}
            </Flex>
            <ContentColumn as={Link} to={post.url} textDecoration="none">
                <Typography variant="caption" lines={2} wordBreak="break-word">
                    {post.text}
                </Typography>
                <Typography variant="caption" color="secondary">
                    {getSourceDisplayText(post.source)}
                </Typography>
            </ContentColumn>
            <DateColumn as={Link} to={post.url} textDecoration="none" alignSelf="stretch">
                <Typography textAlign="right" variant="caption">
                    {formatDistanceToNow(post.createdAt)}
                </Typography>
            </DateColumn>
            <ControlColumn>
                {renderMenu(
                    <IconButton size="s">
                        <IconKebab />
                    </IconButton>,
                )}
            </ControlColumn>
        </Row>
    );
};

interface FeedManagementRowSkeletonProps extends Omit<ComponentProps<typeof Flex>, 'onSelect'> {}

export const FeedManagementRowSkeleton = (props: FeedManagementRowSkeletonProps) => (
    <Row {...props}>
        <Indicator background="var(--placeholder)" />
        <ControlColumn>
            <Skeleton variant="rect" width={24} height={24} borderRadius={1} />
        </ControlColumn>
        <Skeleton variant="rect" width={64} height={64} borderRadius={1} />
        <ContentColumn>
            <Box width="100%">
                <Skeleton variant="text" width="100%" height={14} />
                <Skeleton variant="text" width="80%" height={14} />
            </Box>
            <Skeleton variant="text" width={100} height={14} />
        </ContentColumn>
        <DateColumn>
            <Skeleton variant="text" width="80%" height={14} />
        </DateColumn>
        <ControlColumn>
            <Skeleton variant="rect" width={24} height={24} borderRadius={1} />
        </ControlColumn>
    </Row>
);
